import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from "@mui/material";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../Dialog/DialogHeaderWithClose.component";
import { SeriesBookingScope } from "../../features/Schedule/series-booking.types";
import { isSeriesDialogBackdropCloseReason } from "./functions/is-series-dialog-backdrop-close-reason.function";

type P = {
  open: boolean;
  selectedDateIso: string;
  timezone: string;
  titleKey: string;
  bodyKey: string;
  confirmLabelKey: string;
  confirmColor?: "error" | "primary";
  onClose: () => void;
  onConfirm: (scope: SeriesBookingScope) => void;
};

type ScopeOptionLabelProps = {
  title: string;
  description: string;
};

function ScopeOptionLabel({ title, description }: Readonly<ScopeOptionLabelProps>) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography component="span" variant="body1" display="block">
        {title}
      </Typography>
      <Typography component="span" variant="body2" color="text.secondary" display="block">
        {description}
      </Typography>
    </Box>
  );
}

export function ScheduleSeriesScopeDialog({
  open,
  selectedDateIso,
  timezone,
  titleKey,
  bodyKey,
  confirmLabelKey,
  confirmColor = "primary",
  onClose,
  onConfirm
}: Readonly<P>) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<SeriesBookingScope>(SeriesBookingScope.OCCURRENCE);

  useEffect(() => {
    if (open) {
      setScope(SeriesBookingScope.OCCURRENCE);
    }
  }, [open]);

  const formattedDate = DateTime.fromISO(selectedDateIso).setZone(timezone).toFormat("ccc, d LLL");

  const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (isSeriesDialogBackdropCloseReason(reason)) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="schedule-series-scope-title"
      data-testid="schedule-series-scope-dialog"
    >
      <DialogHeaderWithClose
        titleId="schedule-series-scope-title"
        title={t(titleKey)}
        onClose={onClose}
        closeTestId="schedule-series-scope-close"
      />
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t(bodyKey)}</DialogContentText>
        <FormControl>
          <RadioGroup
            value={scope}
            onChange={event => setScope(event.target.value as SeriesBookingScope)}
            data-testid="schedule-series-scope-options"
          >
            <FormControlLabel
              value={SeriesBookingScope.OCCURRENCE}
              control={<Radio />}
              sx={{ alignItems: "flex-start", mb: 1 }}
              label={
                <ScopeOptionLabel
                  title={t("_schedule_seriesScopeOnlyThisDay", { date: formattedDate })}
                  description={t("_schedule_seriesScopeOnlyThisDayHint")}
                />
              }
              data-testid="schedule-series-scope-occurrence"
            />
            <FormControlLabel
              value={SeriesBookingScope.THIS_AND_FUTURE}
              control={<Radio />}
              sx={{ alignItems: "flex-start", mb: 1 }}
              label={
                <ScopeOptionLabel
                  title={t("_schedule_seriesScopeThisAndLater")}
                  description={t("_schedule_seriesScopeThisAndLaterHint")}
                />
              }
              data-testid="schedule-series-scope-this-and-future"
            />
            <FormControlLabel
              value={SeriesBookingScope.ENTIRE_SERIES}
              control={<Radio />}
              sx={{ alignItems: "flex-start" }}
              label={
                <ScopeOptionLabel
                  title={t("_schedule_seriesScopeEntireSeries")}
                  description={t("_schedule_seriesScopeEntireSeriesHint")}
                />
              }
              data-testid="schedule-series-scope-entire-series"
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="schedule-series-scope-cancel" color="secondary">
          {t("Cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(scope)}
          variant="contained"
          color={confirmColor}
          data-testid="schedule-series-scope-confirm"
        >
          {t(confirmLabelKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
